import { useNavigate, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Award,
  Coins,
  Layers,
  RefreshCw,
  ShieldAlert,
  Sliders,
} from "lucide-react";
import type {
  ThreadResponse,
  BatchResponse,
  EvaluationResponse,
  CommentResponse,
} from "../../gen/models";
import type { TelemetrySummary } from "./useThreadData";
import { formatChannelName } from "./threadUtils";
import { TimeAgo } from "../common/TimeAgo";
import { useUserDirectory } from "../../hooks/useUserDirectory";
import { useStickyPopover } from "../../hooks/useStickyPopover";

interface ThreadHeaderProps {
  thread: ThreadResponse;
  compositeScore?: number | null;
  isFlagged?: boolean;
  hasHallucination?: boolean;
  activeBatchId: string;
  activeBatches: BatchResponse[];
  allBatches?: BatchResponse[];
  evaluations: EvaluationResponse[];
  comments: CommentResponse[];
  commentBatchFilter: string;
  telemetrySummary?: TelemetrySummary;
  loading: boolean;
  isRefreshing?: boolean;
  onRefresh: () => void;
  onSelectBatchId: (id: string) => void;
  onSelectCommentBatchFilter: (batchId: string) => void;
  onOpenBatchModal: () => void;
  onOpenSimulator?: () => void;
  onOpenReviewDrawer: () => void;
  onOpenEvaluationsList?: () => void;
}

export function ThreadHeader({
  thread,
  compositeScore,
  isFlagged,
  hasHallucination,
  activeBatchId,
  activeBatches,
  allBatches = [],
  evaluations,
  comments,
  commentBatchFilter,
  telemetrySummary,
  loading,
  isRefreshing = false,
  onRefresh,
  onSelectBatchId,
  onSelectCommentBatchFilter,
  onOpenBatchModal,
  onOpenReviewDrawer,
  onOpenEvaluationsList,
}: ThreadHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAgentDisplayName, sanitizeProtocoloName } = useUserDirectory();
  const displayBatches = allBatches.length > 0 ? allBatches : activeBatches;
  const costPopover = useStickyPopover();

  const displayAgentName = getAgentDisplayName(thread.agent_type, thread.agent_name);
  const displayTitle = thread.title ? sanitizeProtocoloName(thread.title, "Sofia") : null;

  const formatLatency = (ms: number) => {
    if (!ms || ms <= 0) return "0ms";
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(1)}s`;
    }
    return `${Math.round(ms)}ms`;
  };

  return (
    <header className="shrink-0 w-full border-b border-stone-200/50 dark:border-stone-800/50 px-4 md:px-6 py-2.5 bg-stone-50/80 dark:bg-stone-950/80 backdrop-blur-xs">
      <div className="max-w-5xl mx-auto space-y-2">
        {/* Row 1: Nav + Meta & Actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
          {/* Left: Thread info + Telemetry stats */}
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(`/threads${location.search}`)}
                className="md:hidden inline-flex items-center gap-1.5 px-2 py-0.5 -ml-1 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white rounded-md hover:bg-stone-200/60 dark:hover:bg-stone-800 transition shrink-0"
                title="Back to conversations list"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Conversations</span>
              </button>
              {displayTitle ? (
                <>
                  <h1 className="text-sm font-bold text-stone-900 dark:text-stone-100 truncate max-w-md">
                    {displayTitle}
                  </h1>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 border border-stone-200 dark:border-stone-700">
                    {displayAgentName}
                  </span>
                  <span className="font-mono text-xs text-stone-400 dark:text-stone-500" title={thread.id}>
                    #{thread.id.slice(0, 8)}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-stone-600 dark:text-stone-400">
                    {displayAgentName}
                  </span>
                  <span className="font-mono text-xs text-stone-300 dark:text-stone-600" title={thread.id}>
                    {thread.id.slice(0, 16)}...
                  </span>
                </>
              )}

              {compositeScore != null ? (
                <button
                  type="button"
                  onClick={onOpenEvaluationsList}
                  className="inline-flex items-center gap-1 font-semibold text-emerald-500 dark:text-emerald-400 text-xs hover:underline cursor-pointer"
                  title="View evaluations recorded on this thread"
                >
                  <Award className="w-3 h-3" />
                  <span>{compositeScore.toFixed(2)}</span>
                </button>
              ) : (
                <span className="text-stone-500 text-[11px]">Unrated</span>
              )}

              {isFlagged && (
                <span className="inline-flex items-center gap-1 font-semibold text-rose-500 text-[11px]">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Flagged</span>
                </span>
              )}

              {hasHallucination && (
                <span className="inline-flex items-center gap-1 font-semibold text-rose-500 text-[11px]">
                  <ShieldAlert className="w-3 h-3" />
                  <span>Hallucination</span>
                </span>
              )}
            </div>

            {/* Telemetry stats under conversation info */}
            {telemetrySummary && telemetrySummary.runsCount > 0 && (
              <div className="flex items-center gap-2 text-[11px] text-stone-500 dark:text-stone-400">
                <span>
                  Tokens:{" "}
                  <strong className="text-stone-700 dark:text-stone-200 font-medium">
                    {telemetrySummary.totalTokens.toLocaleString()}
                  </strong>
                  {telemetrySummary.totalTokensIn > 0 && (
                    <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono ml-1">
                      ({telemetrySummary.totalTokensIn.toLocaleString()} in / {telemetrySummary.totalTokensOut.toLocaleString()} out)
                    </span>
                  )}
                  {telemetrySummary.totalCachedTokens > 0 && (
                    <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-mono ml-1" title={`${telemetrySummary.totalCachedTokens.toLocaleString()} cached tokens across runs`}>
                      ⚡{telemetrySummary.totalCachedTokens.toLocaleString()} cached
                    </span>
                  )}
                </span>
                <span>•</span>
                <div
                  ref={costPopover.containerRef}
                  className="relative inline-flex items-center"
                  onMouseEnter={costPopover.handleMouseEnter}
                  onMouseLeave={costPopover.handleMouseLeave}
                >
                  <button
                    type="button"
                    onClick={costPopover.handleToggleClick}
                    className="inline-flex items-center gap-1 cursor-pointer hover:text-stone-700 dark:hover:text-stone-200 transition-colors focus:outline-hidden"
                    title={
                      costPopover.isPinned
                        ? "Breakdown pinned (click to unpin)"
                        : "Click to pin or hover to inspect cost and run breakdown"
                    }
                  >
                    <span>
                      Cost:{" "}
                      <strong className="text-stone-400 dark:text-stone-300 font-medium underline decoration-stone-400/50 dark:decoration-stone-600 decoration-dotted underline-offset-3">
                        ${telemetrySummary.totalCostUsd.toFixed(4)}
                      </strong>
                    </span>
                  </button>

                  {/* Hover / Click Breakdown Popover */}
                  {costPopover.isOpen && (
                    <div
                      className="absolute top-full left-0 mt-2 w-80 md:w-96 rounded-xl border border-stone-200 dark:border-stone-800 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md p-3.5 shadow-xl z-50 text-xs animate-in fade-in duration-100"
                    >
                      <div className="flex items-center justify-between pb-2 border-b border-stone-100 dark:border-stone-800">
                        <div className="flex items-center gap-1.5 font-medium text-stone-800 dark:text-stone-200">
                          <Coins className="w-3.5 h-3.5 text-amber-500" />
                          <span>Cost & Run Breakdown</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {costPopover.isPinned && (
                            <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 rounded">
                              Pinned
                            </span>
                          )}
                          <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono">
                            {telemetrySummary.runsCount} {telemetrySummary.runsCount === 1 ? "run" : "runs"}
                          </span>
                        </div>
                      </div>

                      <div className="py-2 space-y-2">
                        {telemetrySummary.breakdown && telemetrySummary.breakdown.length > 0 ? (
                          telemetrySummary.breakdown.map((item) => (
                            <div key={item.key} className="flex flex-col gap-0.5">
                              <div className="flex items-center justify-between font-medium">
                                <span className="flex items-center gap-1.5 text-stone-700 dark:text-stone-300">
                                  <span
                                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                      item.agentType === "feedback"
                                        ? "bg-indigo-500"
                                        : item.agentType === "sales"
                                        ? "bg-emerald-500"
                                        : "bg-amber-500"
                                    }`}
                                  />
                                  <span className="truncate">{item.label}</span>
                                </span>
                                <span className="font-mono text-stone-900 dark:text-stone-100 shrink-0 ml-2">
                                  ${item.costUsd.toFixed(4)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pl-3 font-mono">
                                <span>
                                  {item.tokens.toLocaleString()} tok
                                  {item.tokensIn > 0 ? ` (${item.tokensIn.toLocaleString()} in / ${item.tokensOut.toLocaleString()} out)` : ""}
                                </span>
                                <span>
                                  {item.runsCount} {item.runsCount === 1 ? "run" : "runs"} • {formatLatency(item.avgDurationMs)}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-stone-400 text-center py-2 text-[11px]">No breakdown available</div>
                        )}
                      </div>

                      <div className="pt-2 border-t border-stone-100 dark:border-stone-800 space-y-1">
                        <div className="flex items-center justify-between font-semibold text-stone-900 dark:text-stone-100">
                          <span>Total</span>
                          <div className="flex items-center gap-2 font-mono text-[11px]">
                            <span>
                              {telemetrySummary.totalTokens.toLocaleString()} tok
                              {telemetrySummary.totalTokensIn > 0 ? ` (${telemetrySummary.totalTokensIn.toLocaleString()} in / ${telemetrySummary.totalTokensOut.toLocaleString()} out)` : ""}
                            </span>
                            <span>•</span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-bold">${telemetrySummary.totalCostUsd.toFixed(4)}</span>
                          </div>
                        </div>
                        {telemetrySummary.totalCachedTokens > 0 && (
                          <div className="text-[10px] text-indigo-600 dark:text-indigo-400 font-mono text-right">
                            ⚡ {telemetrySummary.totalCachedTokens.toLocaleString()} prompt tokens served from cache
                          </div>
                        )}
                        <p className="text-[10px] text-stone-400 dark:text-stone-500 leading-tight">
                          Aggregates all execution runs for this conversation, including model generation and automated turn evaluation.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <span>•</span>
                <span>
                  Latency:{" "}
                  <strong className="text-stone-400 font-normal">
                    {formatLatency(telemetrySummary.avgLatencyMs)}
                  </strong>
                </span>
              </div>
            )}
          </div>

          {/* Right: Action Buttons */}
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={onOpenReviewDrawer}
              className="inline-flex items-center gap-1 text-xs font-medium text-stone-400 hover:text-stone-200 transition cursor-pointer hover:underline"
            >
              <Sliders className="w-3 h-3 text-stone-400" />
              <span>Review</span>
            </button>

            {/* Fixed-width container prevents layout shift between 'live' (4 chars) and 'syncing' (7 chars) */}
            <div className="w-[58px] shrink-0 flex items-center justify-start">
              {isRefreshing ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-emerald-500/90 dark:text-emerald-400/90 transition-opacity">
                  <span className="relative flex h-1.5 w-1.5 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                  </span>
                  <span>syncing</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-stone-400 dark:text-stone-500">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-stone-300 dark:bg-stone-700" />
                  <span>live</span>
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={onRefresh}
              disabled={loading || isRefreshing}
              className="text-stone-400 hover:text-stone-200 p-0.5 transition cursor-pointer"
              title={isRefreshing ? "Auto-refreshing..." : "Refresh thread"}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading || isRefreshing ? "animate-spin text-emerald-500" : ""}`} />
            </button>
          </div>
        </div>

        {/* Row 2: Secondary Ribbon Info */}
        <div className="flex flex-wrap items-center gap-3 pt-0.5 text-[11px] text-stone-500">
          <span>Channel: <strong className="text-stone-400 font-normal" title={`Channel ID: ${thread.source_channel_id}`}>{formatChannelName(thread.source_channel_id, thread.metadata)}</strong></span>
          <span>•</span>
          <span>
            Started:{" "}
            <strong className="text-stone-400 font-normal">
              {new Date(thread.created_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}{" "}
              (<TimeAgo date={thread.created_at} titlePrefix="Started: " />)
            </strong>
          </span>
          <span>•</span>
          <button
            type="button"
            onClick={onOpenEvaluationsList}
            className="inline-flex items-center gap-1 hover:text-stone-300 dark:hover:text-stone-200 transition cursor-pointer group text-left"
            title="View evaluations recorded on this thread"
          >
            <span>Evaluations:</span>
            <strong className="text-stone-400 font-normal group-hover:text-stone-200 underline decoration-stone-500/50 underline-offset-2">
              {evaluations.length} recorded
            </strong>
          </button>
          <span>•</span>
          <span>Comments: <strong className="text-stone-400 font-normal">{comments.length} open</strong></span>
        </div>

        {/* Row 3: Filter & Target Batch Floating/Attached Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-6 items-center pt-0.5 text-[11px] text-stone-500">
          {/* Filter Comments by Batch */}
          <div className="lg:col-span-8 flex items-center">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-stone-200/80 dark:border-stone-800/80 bg-white/70 dark:bg-stone-900/70 shadow-xs backdrop-blur-xs text-xs text-stone-400">
              <span className="text-[11px] text-stone-500 font-medium whitespace-nowrap">Show notes:</span>
              <select
                value={commentBatchFilter}
                onChange={(e) => onSelectCommentBatchFilter(e.target.value)}
                className="bg-transparent text-stone-700 dark:text-stone-300 text-xs font-medium focus:outline-hidden cursor-pointer"
              >
                <option value="all" className="bg-white dark:bg-stone-900">All Batches</option>
                <option value="unbatched" className="bg-white dark:bg-stone-900">Unassigned Only</option>
                {displayBatches.map((b) => (
                  <option key={b.id} value={b.id} className="bg-white dark:bg-stone-900">
                    {b.title}{b.status !== "collecting" ? ` (${b.status})` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Target Batch Floating Panel attached to top bar in comments column */}
          <div className="lg:col-span-4 flex items-center justify-end">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-stone-200/80 dark:border-stone-800/80 bg-white/70 dark:bg-stone-900/70 shadow-xs backdrop-blur-xs text-xs text-stone-400">
              <Layers className="w-3.5 h-3.5 text-stone-500 shrink-0" />
              <span className="text-[11px] text-stone-500 font-medium whitespace-nowrap">Target Batch:</span>
              <select
                value={activeBatchId}
                onChange={(e) => onSelectBatchId(e.target.value)}
                className="bg-transparent text-stone-700 dark:text-stone-300 text-xs font-medium focus:outline-hidden cursor-pointer max-w-[140px] truncate"
              >
                <option value="" className="bg-white dark:bg-stone-900">
                  None
                </option>
                {activeBatches.map((b) => (
                  <option key={b.id} value={b.id} className="bg-white dark:bg-stone-900">
                    {b.title}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={onOpenBatchModal}
                className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer whitespace-nowrap"
              >
                + New
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
